import type { AboutSectionContent, ScrollRevealConfig } from "@site-factory/config";
import { cn } from "../utils";
import { SectionTitle, BodyText } from "../typography/Typography";
import { BandedSection, type BandedSectionVariant } from "../components/BandedSection";
import { AnimateOnScroll, CountUp } from "../animations";
import { VideoEmbed } from "../components/VideoEmbed";

export interface AboutSectionProps extends AboutSectionContent {
  className?: string;
  bandVariant?: BandedSectionVariant;
  /** Optional scroll reveal animation configuration */
  scrollReveal?: ScrollRevealConfig;
  /** Enable animated stat counters (default: true) */
  animateStats?: boolean;
}

/**
 * Parse a stat value string to extract number, prefix, and suffix.
 * Examples: "500+" => { value: 500, suffix: "+" }
 *           "$1M" => { value: 1, prefix: "$", suffix: "M" }
 *           "100%" => { value: 100, suffix: "%" }
 */
function parseStatValue(valueStr: string): {
  value: number;
  prefix: string;
  suffix: string;
} {
  const match = valueStr.match(/^([^\d]*)(\d+(?:\.\d+)?)([^\d]*)$/);
  if (!match) {
    return { value: parseInt(valueStr, 10) || 0, prefix: "", suffix: "" };
  }
  return {
    value: parseFloat(match[2]),
    prefix: match[1],
    suffix: match[3],
  };
}

export function AboutSection({
  title,
  description,
  image,
  video,
  stats,
  variant = "imageRight",
  className,
  bandVariant = "plain",
  scrollReveal = { enabled: true, variant: "fadeUp" },
  animateStats = true,
}: AboutSectionProps) {
  const imageOnLeft = variant === "imageLeft";
  const isCentered = variant === "centered";

  return (
    <BandedSection variant={bandVariant} className="py-16 md:py-24" contentClassName={className}>
      {isCentered ? (
        <AnimateOnScroll {...scrollReveal}>
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <SectionTitle>{title}</SectionTitle>
            <BodyText className="text-neutral-700">{description}</BodyText>
            {video ? (
              <div className="mt-6">
                <VideoEmbed {...video} />
              </div>
            ) : image ? (
              <div className="relative mt-6">
                <div className="absolute inset-0 rounded-[16px] bg-primary/10 blur-3xl" aria-hidden="true" />
                <img
                  src={image}
                  alt={title}
                  className="relative mt-4 rounded-[16px] shadow-lg mx-auto w-full max-w-3xl object-cover"
                />
              </div>
            ) : null}
          </div>
        </AnimateOnScroll>
      ) : (
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <AnimateOnScroll
            className={cn(imageOnLeft && "md:order-2", "space-y-4")}
            {...scrollReveal}
          >
            <SectionTitle>{title}</SectionTitle>
            <BodyText className="text-neutral-700">{description}</BodyText>

            {stats && stats.length > 0 && (
              <div className="grid grid-cols-2 gap-8 mt-4">
                {stats.map((stat, index) => {
                  const parsed = parseStatValue(stat.value);
                  return (
                    <div
                      key={index}
                      className="p-4 rounded-[12px] bg-surface-alt border border-neutral-200 shadow-sm"
                    >
                      <p className="text-3xl font-bold text-primary">
                        {animateStats ? (
                          <CountUp
                            to={parsed.value}
                            prefix={parsed.prefix}
                            suffix={parsed.suffix}
                            duration={2}
                            delay={index * 0.1}
                          />
                        ) : (
                          stat.value
                        )}
                      </p>
                      <p className="text-neutral-600">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </AnimateOnScroll>

          {(video || image) && (
            <AnimateOnScroll
              className={cn(imageOnLeft && "md:order-1")}
              variant="fadeLeft"
              duration={0.8}
            >
              <div className="relative">
                <div className="absolute inset-0 rounded-[16px] bg-accent/10 blur-3xl" aria-hidden="true" />
                {video ? (
                  <VideoEmbed {...video} />
                ) : (
                  <img
                    src={image!}
                    alt={title}
                    className="relative rounded-[16px] shadow-lg w-full object-cover"
                  />
                )}
              </div>
            </AnimateOnScroll>
          )}
        </div>
      )}
    </BandedSection>
  );
}
